import type { PassLimits } from '../interfaces/visitor-pass';

/**
 * WHAT A FAILED PASS CALL MEANS, IN THE PRODUCT'S WORDS (resident-portal
 * RP-9.6).
 *
 * One reader for every pass action — the editor's Save, pause / resume / end /
 * skip, share, stop sharing, "Not now" on a suggestion, the IQ fill — so a
 * refusal reads the same wherever it is met. It lived in the pass editor; three
 * screens beside it answered every failure with "Check your connection", which
 * blamed the reader's network for a rate limit, an ended pass and a switched-off
 * building alike (QA-2026-10-05 Q-17).
 *
 * THE RULES
 *   no response at all        the ONLY case that mentions the connection
 *   400 {field, fields}       field messages (the server writes those as copy)
 *   402 SUBSCRIPTION_ENDED    the subscription line
 *   403                       no permission
 *   404                       the caller's "not here any more" — and the caller
 *                             re-reads its page (`gone` says so)
 *   409 PASS_PENDING / PASSES_DISABLED / PASS_LIMIT / SKIP_LIMIT / PASS_CHANGED
 *                             each its own sentence
 *   429 PASS_DAILY_LIMIT      the daily line; any other 429 the rate-limit line
 *   anything else             the caller's own "Could not …. Try again."
 *
 * Never the server's or axios's text for anything but a field message.
 */

export type PassFieldMessages = Partial<Record<string, string>>;

export const PASS_ERROR_COPY = {
  offline: 'Could not reach Lobbero. Check your connection and try again.',
  failed: 'Something went wrong. Try again.',
  tooMany: 'Too many changes just now. Try again in a few minutes.',
  tooManyReads: 'Too many requests just now. Wait a few minutes, then try again.',
  // The server's own sentences (src/passes/errors.js), word for word.
  dailyLimit: 'Too many new visitor passes for this unit today. Try again tomorrow.',
  pending:
    "This visitor pass is waiting for the building's approval. You can end it, or wait for the answer.",
  changed: 'This request changed since you opened it. Read it again.',
  subscriptionEnded: "Your building's Lobbero subscription is inactive.",
  passesDisabled: 'Visitor passes are switched off for this building.',
  passLimit: (max: number) =>
    `This unit already has ${max} live visitor passes. End one before adding another.`,
  skipLimit: (max: number) =>
    `This pass already skips ${max} future dates. Undo a skip before adding another.`,
  forbidden: "You don't have permission to change visitor passes.",
  invalid: "This visitor pass isn't valid. Check the details and try again.",
  gone: 'This pass is not here any more.',
} as const;

type ErrorBody = { message?: unknown; field?: unknown; fields?: unknown; code?: unknown };

type ErrorLike = {
  isAxiosError?: unknown;
  request?: unknown;
  code?: unknown;
  message?: unknown;
  response?: {
    status?: number;
    data?: ErrorBody;
  };
};

export interface PassErrorReading {
  /** The HTTP status, or null when no answer came back. */
  status: number | null;
  code: string | null;
  /** Field -> message, when the server named fields (a 400). */
  fields: PassFieldMessages;
  /** A sentence of OUR copy for everything that is not a field message. */
  message: string;
  /** The request never got an answer: the one case that is about the network. */
  offline: boolean;
  /** 404 — what was acted on is no longer there. The caller re-reads its page. */
  gone: boolean;
  /** 429 — too many, of whichever kind. Retrying at once will not help. */
  limited: boolean;
}

const NETWORK_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ERR_INTERNET_DISCONNECTED']);

/**
 * True when a request was sent and nothing came back. An error thrown by our
 * own code has no `response` either, and is NOT this: telling somebody to check
 * their connection because a screen threw would be the old bug again.
 */
export const isNoResponse = (error: unknown): boolean => {
  const e = (error || {}) as ErrorLike;
  if (e.response) return false;

  return (
    e.isAxiosError === true ||
    e.request !== undefined ||
    (typeof e.code === 'string' && NETWORK_CODES.has(e.code)) ||
    e.message === 'Network Error'
  );
};

export interface PassErrorContext {
  /** The server's limits, for the two sentences that quote one. */
  limits?: Pick<PassLimits, 'max_live_per_unit' | 'skip_max'> | null;
  /** "Could not pause Rosa's pass. Try again." — never about the connection. */
  fallback?: string;
  /** What a 404 means here. */
  notFound?: string;
  /** The line for "no answer came back", when the caller has a better one. */
  offline?: string;
  /** A 429 on a READ: "requests", not "changes". */
  reading?: boolean;
}

export const readPassError = (error: unknown, ctx: PassErrorContext = {}): PassErrorReading => {
  const response = (error as ErrorLike)?.response;
  const status = typeof response?.status === 'number' ? response.status : null;
  const data: ErrorBody = response?.data && typeof response.data === 'object' ? response.data : {};
  const code = typeof data.code === 'string' ? data.code : null;
  const fields: PassFieldMessages = {};
  const offline = isNoResponse(error);
  const flags = { offline, gone: status === 404, limited: status === 429 };

  if (status === 400) {
    if (data.fields && typeof data.fields === 'object') {
      Object.entries(data.fields as Record<string, unknown>).forEach(([key, value]) => {
        if (typeof value === 'string' && value) fields[key] = value;
      });
    }
    if (
      !Object.keys(fields).length &&
      typeof data.field === 'string' &&
      data.field &&
      typeof data.message === 'string'
    ) {
      fields[data.field] = data.message;
    }

    return {
      status,
      code,
      fields,
      message: Object.keys(fields).length ? '' : PASS_ERROR_COPY.invalid,
      ...flags,
    };
  }

  let message: string = ctx.fallback || PASS_ERROR_COPY.failed;
  if (offline) message = ctx.offline || PASS_ERROR_COPY.offline;
  else if (status === 402 && code === 'SUBSCRIPTION_ENDED') message = PASS_ERROR_COPY.subscriptionEnded;
  else if (status === 409 && code === 'PASS_LIMIT') {
    message = PASS_ERROR_COPY.passLimit(ctx.limits?.max_live_per_unit ?? 30);
  } else if (status === 409 && code === 'PASSES_DISABLED') message = PASS_ERROR_COPY.passesDisabled;
  else if (status === 409 && code === 'PASS_PENDING') message = PASS_ERROR_COPY.pending;
  else if (status === 409 && code === 'PASS_CHANGED') message = PASS_ERROR_COPY.changed;
  else if (status === 409 && code === 'SKIP_LIMIT') {
    message = PASS_ERROR_COPY.skipLimit(ctx.limits?.skip_max ?? 60);
  } else if (status === 429 && code === 'PASS_DAILY_LIMIT') message = PASS_ERROR_COPY.dailyLimit;
  else if (status === 429) message = ctx.reading ? PASS_ERROR_COPY.tooManyReads : PASS_ERROR_COPY.tooMany;
  else if (status === 404) message = ctx.notFound || PASS_ERROR_COPY.gone;
  else if (status === 403) message = PASS_ERROR_COPY.forbidden;

  return { status, code, fields, message, ...flags };
};
